import type { FacetedFilterOption } from "@/components/feature/KnowledgebaseFilters"
import { useState } from "react"
import type { PaginatedOut } from "../api/types/masterData"

interface FacetListResult<TRow> {
  data?: { pages: PaginatedOut<TRow>[] }
  fetchNextPage: () => unknown
  hasNextPage?: boolean
  isFetchingNextPage: boolean
}

/**
 * Backs a MasterDataFacet with a real (small, paginated) query instead of
 * preloading every row: search re-queries the API server-side, and only one
 * extra page is fetched at a time via "Load more" — so a facet with 5,000
 * rows behaves the same as one with 5.
 */
export function useFacetOptions<TRow>(
  useList: (params: {
    q?: string
    limit?: number
    enabled?: boolean
  }) => FacetListResult<TRow>,
  toOption: (row: TRow) => FacetedFilterOption,
  /** Set false when this facet's filter drawer can't be shown (e.g. a sibling
   *  view mode with no facets at all) — skips the request entirely instead of
   *  fetching options nobody can select. Defaults to true. */
  enabled = true,
) {
  const [query, setQuery] = useState("")
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useList({
    q: query.trim() || undefined,
    limit: 50,
    enabled,
  })

  // `p.items` can be `undefined` on a malformed page (e.g. a 202-with-empty-body
  // response the underlying list hook didn't normalize) — flatMap would otherwise
  // push a literal `undefined` into the array and crash the very next `.map`.
  const options = (data?.pages.flatMap((p) => p?.items ?? []) ?? []).map(
    toOption,
  )

  return {
    options,
    onQueryChange: (value: string) => setQuery(value.trim()),
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    isLoadingMore: isFetchingNextPage,
  }
}
